import { type JSX, useRef, useState } from 'react';

import { MobiLauncherConfig } from '@schema/index.js';

import { getApiBaseUrl, getStoredToken, publishConfig, setStoredToken } from '../api.js';
import type { BuilderStore } from '../state.js';
import { confirmDialog, promptDialog } from '../../components/dialogs.js';

export interface ToolbarProps {
  store: BuilderStore;
  /** Open the shortcuts cheatsheet modal. */
  onOpenShortcuts: () => void;
  /**
   * When true, hide the admin-only buttons (New/Import/Download/Publish,
   * forget-token, "Autosaved to your browser" hint). Set by the
   * Portal-embedded BuilderEditor — customers shouldn't see the
   * standalone-Builder operator surface.
   */
  embeddedInPortal?: boolean;
}

type ImportFeedback =
  | { kind: 'idle' }
  | { kind: 'ok'; filename: string }
  | { kind: 'error'; message: string }
  | { kind: 'busy'; message: string }
  | { kind: 'published'; tenantId: string; etag: string };

/**
 * Top toolbar: New / Import / Download.
 *
 * Import path: parse the picked file through Zod. If it fails we surface
 * the message inline rather than dropping silently — a broken paste is
 * usually a tenantId mismatch or schemaVersion drift, both of which the
 * admin can fix once they see the error.
 *
 * Download: build a data: URL on the fly and click a synthetic anchor.
 * No backend round-trip — the in-memory config is the source of truth.
 */
export function Toolbar({
  store,
  onOpenShortcuts,
  embeddedInPortal,
}: ToolbarProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [feedback, setFeedback] = useState<ImportFeedback>({ kind: 'idle' });

  function handleNew(): void {
    void (async () => {
      const ok = await confirmDialog({
        title: 'Reset config?',
        message: 'Reset to a blank starter config? Your current draft will be cleared — this cannot be undone.',
        confirmLabel: 'Reset config',
        destructive: true,
        requireText: 'reset',
      });
      if (!ok) return;
      store.reset();
      setFeedback({ kind: 'idle' });
    })();
  }

  function handleDownload(): void {
    const json = JSON.stringify(store.config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeName = store.config.deploymentName
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'mobilauncher-config';
    a.download = `${safeName}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function openImportPicker(): void {
    fileInputRef.current?.click();
  }

  async function handlePublish(): Promise<void> {
    if (store.parseStatus.kind === 'invalid') {
      setFeedback({
        kind: 'error',
        message: 'Config has validation errors — fix before publishing.',
      });
      return;
    }

    let token = getStoredToken();
    if (!token) {
      const entered = await promptDialog({
        title: 'Admin token',
        message:
          `Admin bearer token for ${getApiBaseUrl()}\n\n` +
          'Stored in this browser only. Use the "Forget token" button to clear.',
        confirmLabel: 'Save & publish',
      });
      if (entered === null || entered.trim().length === 0) return;
      token = entered.trim();
      setStoredToken(token);
    }

    setFeedback({ kind: 'busy', message: 'Publishing…' });
    const result = await publishConfig(store.config, token);

    switch (result.kind) {
      case 'ok':
        setFeedback({
          kind: 'published',
          tenantId: store.config.tenantId,
          etag: result.etag,
        });
        return;
      case 'unauthorized':
        setStoredToken('');
        setFeedback({
          kind: 'error',
          message: 'Admin token rejected. Click Publish again to re-enter.',
        });
        return;
      case 'invalidConfig':
        setFeedback({
          kind: 'error',
          message: `Server rejected config: ${result.issues[0]?.message ?? 'schema validation failed'}`,
        });
        return;
      case 'tenantMismatch':
        setFeedback({ kind: 'error', message: result.message });
        return;
      case 'network':
        setFeedback({ kind: 'error', message: `Network: ${result.message}` });
        return;
      case 'unexpected':
        setFeedback({
          kind: 'error',
          message: `Server returned ${String(result.status)}: ${result.message}`,
        });
        return;
    }
  }

  function handleForgetToken(): void {
    setStoredToken('');
    setFeedback({ kind: 'idle' });
  }

  async function handleImportFile(file: File): Promise<void> {
    try {
      const text = await file.text();
      const parsed = MobiLauncherConfig.safeParse(JSON.parse(text));
      if (!parsed.success) {
        setFeedback({
          kind: 'error',
          message: `Invalid config: ${parsed.error.issues[0]?.message ?? 'schema mismatch'}`,
        });
        return;
      }
      store.importConfig(parsed.data);
      setFeedback({ kind: 'ok', filename: file.name });
    } catch (e) {
      setFeedback({
        kind: 'error',
        message: e instanceof Error ? e.message : 'Could not read file.',
      });
    }
  }

  return (
    <div className="builder__toolbar" role="toolbar" aria-label="Config actions">
      <div className="builder__toolbar-group">
        <button
          type="button"
          className="builder__btn builder__btn--icon"
          onClick={store.undo}
          disabled={!store.canUndo}
          title="Undo (⌘Z)"
          aria-label="Undo"
          data-testid="toolbar-undo"
        >
          ↶
        </button>
        <button
          type="button"
          className="builder__btn builder__btn--icon"
          onClick={store.redo}
          disabled={!store.canRedo}
          title="Redo (⇧⌘Z)"
          aria-label="Redo"
          data-testid="toolbar-redo"
        >
          ↷
        </button>
        {!embeddedInPortal && (
          <>
            <span className="builder__toolbar-divider" aria-hidden />
            <button
              type="button"
              className="builder__btn"
              onClick={handleNew}
              data-testid="toolbar-new"
            >
              New
            </button>
            <button
              type="button"
              className="builder__btn"
              onClick={openImportPicker}
              data-testid="toolbar-import"
            >
              Import…
            </button>
            <button
              type="button"
              className="builder__btn"
              onClick={handleDownload}
              data-testid="toolbar-download"
            >
              Download
            </button>
            <button
              type="button"
              className="builder__btn builder__btn--primary"
              onClick={() => {
                void handlePublish();
              }}
              disabled={store.parseStatus.kind === 'invalid'}
              title={
                store.parseStatus.kind === 'invalid'
                  ? 'Fix validation errors before publishing'
                  : `Publish to ${getApiBaseUrl()}`
              }
              data-testid="toolbar-publish"
            >
              Publish
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="builder__toolbar-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImportFile(file);
                // Reset so the same file can be re-picked.
                e.target.value = '';
              }}
              data-testid="toolbar-import-input"
            />
          </>
        )}
      </div>

      <div className="builder__toolbar-status" aria-live="polite">
        {!embeddedInPortal && feedback.kind === 'ok' && (
          <span className="builder__toolbar-msg builder__toolbar-msg--ok">
            Imported {feedback.filename}
          </span>
        )}
        {!embeddedInPortal && feedback.kind === 'busy' && (
          <span className="builder__toolbar-msg builder__toolbar-msg--hint">
            {feedback.message}
          </span>
        )}
        {!embeddedInPortal && feedback.kind === 'published' && (
          <span className="builder__toolbar-msg builder__toolbar-msg--ok">
            Published <code>{feedback.tenantId}</code> · etag {feedback.etag.slice(0, 8)}…
          </span>
        )}
        {!embeddedInPortal && feedback.kind === 'error' && (
          <span className="builder__toolbar-msg builder__toolbar-msg--err">
            {feedback.message}
          </span>
        )}
        {!embeddedInPortal && feedback.kind === 'idle' && (
          <span className="builder__toolbar-msg builder__toolbar-msg--hint">
            Autosaved to your browser
          </span>
        )}
        {!embeddedInPortal && getStoredToken() !== null && (
          <button
            type="button"
            className="builder__btn builder__btn--icon"
            onClick={handleForgetToken}
            title="Clear the admin token from this browser"
            aria-label="Forget admin token"
            data-testid="toolbar-forget-token"
          >
            🔑
          </button>
        )}
        <button
          type="button"
          className="builder__btn builder__btn--icon"
          onClick={onOpenShortcuts}
          aria-label="Keyboard shortcuts"
          title="Keyboard shortcuts (?)"
          data-testid="toolbar-shortcuts"
        >
          ?
        </button>
      </div>
    </div>
  );
}
